import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["badge"]

  connect() {
    console.log('Notifications controller connected')
    
    // Initialize previous count as null to avoid false notification on first load
    this.previousCount = null
    this.isFirstCheck = true
    
    // Attach click handlers to initial notification items
    this.attachNotificationClickHandlers()
    
    // Get initial count
    this.checkForNewNotifications()
    
    // Poll for new notifications every 30 seconds
    this.pollInterval = setInterval(() => {
      this.checkForNewNotifications()
    }, 30000)

    // Listen for custom notification events
    this.boundHandleNewNotification = this.handleNewNotification.bind(this)
    document.addEventListener('notification:received', this.boundHandleNewNotification)
  }

  disconnect() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval)
    }
    if (this.notificationToastTimer) {
      clearTimeout(this.notificationToastTimer)
    }
    document.getElementById('app_notification_toast')?.remove()
    document.removeEventListener('notification:received', this.boundHandleNewNotification)
  }

  async checkForNewNotifications() {
    try {
      console.log('Checking for new notifications...')
      const response = await fetch('/notifications/unread_count')
      const data = await response.json()
      const newCount = data.count
      console.log(`Previous count: ${this.previousCount}, New count: ${newCount}, First check: ${this.isFirstCheck}`)
      
      // On first check, just store the count without triggering notifications
      if (this.isFirstCheck) {
        this.previousCount = newCount
        this.isFirstCheck = false
        this.updateBadge(newCount)
        console.log(`First check complete. Stored count: ${newCount}`)
        return
      }
      
      // Check if we have new notifications (count increased)
      if (this.previousCount !== null && newCount > this.previousCount) {
        // New notification(s) received!
        const notificationDiff = newCount - this.previousCount
        console.log(`NEW NOTIFICATION! Diff: ${notificationDiff}`)
        this.showNotificationPopup(notificationDiff)
        this.playNotificationSound()
        
        // Reload the notification dropdown to show new notifications
        await this.reloadNotificationDropdown()
      }
      
      // Update badge and store current count
      this.updateBadge(newCount)
      this.previousCount = newCount
    } catch (error) {
      console.error('Error checking notifications:', error)
    }
  }

  updateBadge(count) {
    if (this.hasBadgeTarget) {
      if (count > 0) {
        this.badgeTarget.style.display = 'block'
      } else {
        this.badgeTarget.style.display = 'none'
      }
    } else if (count > 0) {
      // Create dot if it doesn't exist
      const bellIcon = this.element.querySelector('.fe-bell')
      if (bellIcon) {
        const dot = document.createElement('span')
        dot.className = 'notification-dot'
        dot.setAttribute('data-notifications-target', 'badge')
        dot.style.cssText = `
          position: absolute;
          top: 6px;
          right: 8px;
          width: 10px;
          height: 10px;
          background-color: #dc3545;
          border-radius: 50%;
          border: 2px solid white;
          animation: pulse 2s infinite;
        `
        bellIcon.parentElement.style.position = 'relative'
        bellIcon.parentElement.appendChild(dot)
      }
    }
  }

  async markAsRead(event) {
    event.preventDefault()
    const notificationId = event.currentTarget.dataset.notificationId
    
    if (!notificationId) {
      window.location.href = event.currentTarget.href
      return
    }

    try {
      await fetch(`/notifications/${notificationId}/mark_as_read`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          'Content-Type': 'application/json'
        }
      })

      // Update badge count
      await this.checkForNewNotifications()

      // Navigate to the notification URL
      window.location.href = event.currentTarget.href
    } catch (error) {
      console.error('Error marking notification as read:', error)
      // Navigate anyway
      window.location.href = event.currentTarget.href
    }
  }

  handleNewNotification(event) {
    const { message, type, count } = event.detail
    
    // Update badge (dot)
    this.updateBadge(count)
    
    // Show an independent popup notification without replacing open dialogs
    this.showNotificationPopup(count, message)
    
    // Play notification sound
    this.playNotificationSound()
  }

  showNotificationPopup(count, customMessage = null) {
    console.log(`showNotificationPopup called with count: ${count}, customMessage: ${customMessage}`)
    
    // Determine message based on count
    let message = customMessage
    if (!message) {
      if (count === 1) {
        message = document.querySelector('html').getAttribute('lang') === 'ar' 
          ? 'لديك إشعار جديد' 
          : 'You have a new notification'
      } else {
        message = document.querySelector('html').getAttribute('lang') === 'ar'
          ? `لديك ${count} إشعارات جديدة`
          : `You have ${count} new notifications`
      }
    }

    const title = document.querySelector('html').getAttribute('lang') === 'ar'
      ? 'إشعار جديد'
      : 'New Notification'

    // SweetAlert supports only one active popup. A notification fired through
    // Swal would replace an authorization or procedure modal the doctor is
    // currently using, so notifications render in their own independent toast.
    if (this.notificationToastTimer) clearTimeout(this.notificationToastTimer)
    document.getElementById('app_notification_toast')?.remove()

    const isArabic = document.documentElement.getAttribute('lang') === 'ar'
    const toast = document.createElement('div')
    toast.id = 'app_notification_toast'
    toast.setAttribute('role', 'status')
    toast.setAttribute('aria-live', 'polite')
    toast.style.cssText = `
      position: fixed;
      bottom: 1.5rem;
      ${isArabic ? 'left' : 'right'}: 1.5rem;
      z-index: 2147483647;
      width: min(420px, calc(100vw - 3rem));
      padding: 1rem 1.25rem 1.15rem;
      background: #fff;
      color: #505050;
      border-radius: .65rem;
      box-shadow: 0 .5rem 1.5rem rgba(0, 0, 0, .24);
      display: flex;
      align-items: center;
      gap: 1rem;
      direction: ${isArabic ? 'rtl' : 'ltr'};
      overflow: hidden;
    `

    const icon = document.createElement('span')
    icon.setAttribute('aria-hidden', 'true')
    icon.textContent = 'i'
    icon.style.cssText = `
      flex: 0 0 2.75rem;
      width: 2.75rem;
      height: 2.75rem;
      border: 4px solid #86ddf5;
      border-radius: 50%;
      color: #25bde8;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 1.75rem;
      font-weight: 700;
      font-family: Arial, sans-serif;
    `

    const content = document.createElement('div')
    content.style.cssText = 'min-width: 0; text-align: start;'
    const heading = document.createElement('strong')
    heading.textContent = title
    heading.style.cssText = 'display: block; margin-bottom: .35rem; font-size: 1.1rem;'
    const body = document.createElement('div')
    body.textContent = message
    body.style.cssText = 'font-size: 1rem; line-height: 1.4;'
    content.append(heading, body)

    const progress = document.createElement('span')
    progress.setAttribute('aria-hidden', 'true')
    progress.style.cssText = `
      position: absolute;
      bottom: 0;
      ${isArabic ? 'right' : 'left'}: 0;
      width: 100%;
      height: 4px;
      background: #86ddf5;
      transition: width 5000ms linear;
    `

    toast.append(icon, content, progress)
    document.body.appendChild(toast)
    document.dispatchEvent(new CustomEvent('notification:displayed', {
      detail: { count: count, message: message }
    }))
    window.requestAnimationFrame(() => { progress.style.width = '0' })

    this.notificationToastTimer = window.setTimeout(() => {
      toast.remove()
      this.notificationToastTimer = null
    }, 5000)
  }


  playNotificationSound() {
    try {
      // Use Web Audio API to generate a pleasant bell notification sound
      const audioContext = new (window.AudioContext || window.webkitAudioContext)()
      
      // Create a bell-like sound with three tones (like a notification chime)
      const playTone = (frequency, startTime, duration, volume = 0.3) => {
        const oscillator = audioContext.createOscillator()
        const gainNode = audioContext.createGain()

        oscillator.connect(gainNode)
        gainNode.connect(audioContext.destination)

        oscillator.frequency.value = frequency
        oscillator.type = 'sine'
        
        gainNode.gain.setValueAtTime(volume, startTime)
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration)

        oscillator.start(startTime)
        oscillator.stop(startTime + duration)
      }

      // Create a pleasant three-tone bell sound (ding-ding-ding)
      const now = audioContext.currentTime
      playTone(880, now, 0.15, 0.25)        // First ding (A5)
      playTone(1046, now + 0.15, 0.15, 0.2) // Second ding (C6)
      playTone(1318, now + 0.3, 0.2, 0.15)  // Third ding (E6)
      
    } catch (error) {
      console.log('Could not play notification sound:', error)
    }
  }

  async reloadNotificationDropdown() {
    try {
      console.log('Reloading notification dropdown...')
      
      // Get current locale from URL or HTML lang attribute
      const urlParams = new URLSearchParams(window.location.search)
      const locale = urlParams.get('locale') || document.querySelector('html').getAttribute('lang') || 'ar'
      
      // Fetch updated notifications HTML partial
      const response = await fetch(`/notifications/dropdown?locale=${locale}`, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'text/html'
        }
      })
      
      if (response.ok) {
        const html = await response.text()
        
        // Find the dropdown menu and update its content
        const dropdownMenu = this.element.querySelector('.notifications-menu')
        if (dropdownMenu) {
          dropdownMenu.innerHTML = html
          
          // Re-attach event listeners to new notification items
          this.attachNotificationClickHandlers()
          
          console.log('Notification dropdown reloaded successfully')
        } else {
          console.error('Could not find .notifications-menu element')
        }
      } else {
        console.error('Failed to reload notifications:', response.status)
      }
    } catch (error) {
      console.error('Error reloading notification dropdown:', error)
    }
  }

  attachNotificationClickHandlers() {
    // Find all notification items in the dropdown
    const notificationItems = this.element.querySelectorAll('.notification-item')
    
    notificationItems.forEach(item => {
      // Remove any existing click listeners to avoid duplicates
      const newItem = item.cloneNode(true)
      item.parentNode.replaceChild(newItem, item)
      
      // Add click handler
      newItem.addEventListener('click', async (event) => {
        event.preventDefault()
        const notificationId = newItem.dataset.notificationId
        const href = newItem.getAttribute('href')
        
        console.log('Notification clicked:', { notificationId, href })
        
        if (notificationId) {
          try {
            // Mark as read
            await fetch(`/notifications/${notificationId}/mark_as_read`, {
              method: 'POST',
              headers: {
                'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
                'Content-Type': 'application/json'
              }
            })
            console.log('Notification marked as read')
          } catch (error) {
            console.error('Error marking notification as read:', error)
          }
        }
        
        // Navigate to the notification URL
        if (href && href !== '#') {
          console.log('Navigating to:', href)
          window.location.href = href
        } else {
          console.error('No valid href found')
        }
      })
    })
    
    console.log(`Attached click handlers to ${notificationItems.length} notification items`)
  }
}
